<script lang="ts">
  /**
   * TOP STRIP (plan §13, ledger row SPX-018) — REGIME · ES last · VIX · session clock · macro ribbon,
   * plus §13b's CUSHION chip.
   *
   * THE ROW WAS MINTED PARTIAL BY CONSTRUCTION, and that is the design rather than a shortfall.
   * Measured on the live edge before the mint (receipt: docs/design/TOP_STRIP_DESIGN_BOARD_2026-08-07.md):
   * three of seven elements have inputs today, three do not, and one was dropped for having no
   * dimension at all.
   *
   *   BUILT            ES1! last+change · VIX chip · NY session clock · REGIME (2026-08-08)
   *   VISIBLE-ABSENCE  macro ribbon · CUSHION — each NAMING the route it is waiting on
   *   DROPPED          the mock's SPX cash chip (`/api/es/candles` answers "allowed: ES1!, VIX";
   *                    no dimension exists, so under D-3 a permanent placeholder would teach a
   *                    false belief — the same reasoning that killed the tide's dead controls)
   *
   * ⇒ REGIME'S `NO FEED` SLOT RETIRED ITSELF, WHICH IS WHAT IT WAS BUILT TO DO. `/api/dealer/gex`
   * went live and the slot's own copy named it as the thing it was waiting for. Board:
   * `docs/design/REGIME_DESIGN_BOARD_2026-08-08.md`. Two absences remain and BOTH ARE CORRECT —
   * CUSHION's implied-move source has no owner, the macro-walls route has no owner.
   *
   * ⇒ WHY THIS STRIP IS BUILDABLE WHILE OTHER PANELS WERE NOT: its only two DATA instruments are
   * ES1! and VIX, which are EXACTLY the two symbols `/api/health` returns a per-instrument verdict
   * for. The tide panel was blocked for a day because ITS instrument had no verdict, so it could not
   * tell CLOSED from STALE and went amber on a healthy closed feed. This strip's gaps fail the
   * OTHER way — a missing INPUT to a DEFINED chip, which is honest absence, not a wrong verdict.
   *
   * THE `NO FEED` STATE (desk-ratified `9bda08f1`) — the seventh chip state, and it exists because
   * `StalenessChip`'s six do not include one for "no route exists", and reusing `NO DATA` would lie:
   *
   *     NO DATA   nothing received YET      → implies data is coming → if it persists, BROKEN
   *     NO FEED   no route exists to poll   → implies nobody built it → if it persists, UNFINISHED
   *
   * A trader reading NO DATA on REGIME concludes the feed died. It was never built. Deliberately
   * NOT AMBER: four amber slots on a healthy-but-unfinished cockpit is amber-inflation, and it is
   * how a reader learns to ignore the one amber that matters. Dashed reuses the token package's own
   * grammar (binding rule 1: the flip is DASHED, magnets SOLID), so it is not a new vocabulary.
   * The copy NAMES THE MISSING ROUTE, so each slot retires itself the day that route lands —
   * the same self-retiring pattern as D-3's `root_verified`.
   */
  import { onMount } from "svelte";
  import { headlinePrice, headlineMarker } from "./headlinePrice";
  import {
    fetchCandles, fetchDealer, fetchHealth, fetchMacroWalls, FeedError, formatAge, verdictTone,
    type DealerResponse, type HealthSymbol, type VenueState,
  } from "./api";
  import {
    macroWallsState, macroAgeLabel, macroWallsTitle,
    type MacroWallsPayload,
  } from "./macroWalls";
  import { buildBook } from "./dealer";
  import { sessionAt } from "./session";
  // PanelFreshness owns the rendered CLOSED pill; this import keeps the panel's shared-banner contract explicit.
  import VenueClosedBanner from "./VenueClosedBanner.svelte";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { reportDoorFailure, servedClosure, type DoorFaultEvidence } from "./faultEvidence";
  import { writeStripMeasurement } from "./liveMeasurement";

  interface Props {
    /**
     * Tick-level last price, 1e-9-scaled by the route. Null whenever there is none to offer.
     * ⛔ REQUIRED, NOT OPTIONAL — and this is the arm, not a style preference. GK-s's H2 mutant
     * ("remove the wiring at the mount") could not be driven: with an optional prop, deleting the
     * wiring TYPECHECKS and the strip silently falls back to the bar close forever. Required makes
     * the deletion a COMPILE ERROR. ⇒ *A RULE THAT MATTERS GOES IN A TYPE, AN ARM OR A GATE* — the
     * tests below cover the DECISION; only the type can cover the WIRING.
     */
    tickPrice: number | null;
    /**
     * Age of THAT price's own row, seconds, as the route computed it from the row's vendor_ts.
     * ⛔ REQUIRED alongside `tickPrice` and not merely nice to have: a price the strip cannot place
     * in time is refused, because a bare number silently degrades to "no change" — a fail-soft on
     * the one figure the owner reads at a glance. REQUIRED for the same reason as `tickPrice`:
     * an optional age lets a caller supply a price it cannot date, and nothing would go red.
     */
    tickAgeS: number | null;
    /** Vendor timestamp of the tick displayed by the headline, epoch ms. */
    tickMs: number | null;
    pollMs?: number;
    /**
     * The dealer route gets its OWN, MUCH SLOWER interval and that is sizing, not laziness: the
     * payload is ~254 KB of 787 strikes over a series whose native cadence is 300s, so polling it
     * on the 30s PRICE tick would pull a quarter-megabyte ten times per source update.
     *
     * ⚠ 180s IS ONLY SAFE BECAUSE THE AGE CHIP CORRECTS FOR ELAPSED TIME (see `dealerAgeS`).
     * `age_s` is a snapshot taken at SERVE time, so a naive render understates the age by up to one
     * poll interval — a slow poll would otherwise buy bandwidth with a freshness lie. With the
     * correction the displayed age is right continuously, which is what makes the cheap poll honest
     * rather than merely cheaper. `codex review` flagged the duplicate poller; this is the half of
     * the fix that lives inside this row (the shared-feed half is routed — see the receipt).
     */
    dealerPollMs?: number;
  }
  let {
    pollMs = 30_000,
    dealerPollMs = 180_000,
    tickPrice,
    tickAgeS,
    tickMs,
  }: Props = $props();

  let stripPrice = $state<HTMLElement | null>(null);

  /**
   * THE HEADLINE PRICE — SPX-061 clause (2). The biggest number on the owner's screen, and until
   * today it was the 1m bar close, which changes ONCE PER MINUTE BY CONSTRUCTION and no poll rate
   * fixes.
   *
   * ⛔⛔ WHAT MADE IT A CORRECTNESS DEFECT RATHER THAN A LATENCY ONE, MEASURED ON PRODUCTION
   * 2026-08-11 22:35Z: the 1m spine stalled, this strip rendered `ES1! —` FOR SIX MINUTES, and a
   * tick price 3 SECONDS OLD was on the same screen three inches below it.
   * ⇒ ***A FALLBACK THAT GOES BLANK BESIDE A LIVE SOURCE IS NOT A CONSERVATIVE FALLBACK, IT IS AN
   * UNWIRED ONE.*** The em-dash was the right render for what this strip was allowed to know; the
   * defect was what it was allowed to know.
   *
   * ⚠ STRICTLY ADDITIVE, and deliberately so: the bar-close path and the em-dash are UNTOUCHED
   * below this branch. A stale tick does not blank the strip and does not override a good bar — it
   * simply stops being offered, and the incumbent behaviour resumes exactly as it was.
   * ⭐ AND THE AGE RIDES THE NUMBER, never the panel around it: whichever source wins says so, and
   * a tick says how old it is, because a StalenessChip qualifies a REGION and stays behind when the
   * FIGURE is screenshotted, quoted or remembered.
   */

  let health = $state<HealthSymbol[] | null>(null);
  let esLast = $state<{ close: number; prevClose: number | null } | null>(null);

  // ⛔ THE DECISION MOVED OUT OF THIS FILE, and that is a testability constraint rather than taste:
  // GK-s could not arm it here. Their H1 went red BY THE TYPECHECKER and their H2 never ran at all.
  // `.svelte` is a file this repo's test runner cannot load ⇒ logic placed here is UNARMED BY
  // CONSTRUCTION (h's law, the one `formingBar.ts` exists for). `headlinePrice.test.ts` drives it.
  let headline = $derived(
    headlinePrice({ tickPrice, tickAgeS, barClose: esLast?.close ?? null }),
  );
  let headlineMark = $derived(headlineMarker(headline));
  $effect(() => {
    if (headline.px === null) return;
    writeStripMeasurement(stripPrice, headline.px, tickMs);
  });

  let vixLast = $state<{ close: number; prevClose: number | null } | null>(null);
  let healthFault = $state<DoorFaultEvidence | null>(null);
  let esFault = $state<DoorFaultEvidence | null>(null);
  let vixFault = $state<DoorFaultEvidence | null>(null);
  let healthClockMs = $state<number | null>(null);
  let esClockMs = $state<number | null>(null);
  let vixClockMs = $state<number | null>(null);
  let dealer = $state<DealerResponse | null>(null);
  let dealerError = $state<string | null>(null);
  /** Our own clock reading at the instant the payload arrived — see `dealerAgeS`. */
  let dealerFetchedMs = $state<number | null>(null);
  let nowMs = $state(Date.now());
  let infoOpen = $state(false);

  /**
   * MACRO (R485(2b)). Its own payload and its own error, deliberately NOT folded into `error`:
   * this strip already learned that lesson — a shared error variable lets either feed's outage
   * misreport the other as broken. The MACRO chip fails on its own evidence or not at all.
   */
  let macro = $state<MacroWallsPayload | null>(null);
  let macroError = $state<string | null>(null);
  let macroState = $derived(macroWallsState(macro, macroError));
  let stripFaults = $derived(
    [healthFault, esFault, vixFault].filter((fault): fault is DoorFaultEvidence => fault !== null),
  );

  const symbolOf = (s: string) => health?.find((h) => h.symbol === s) ?? null;
  let es = $derived(symbolOf("ES1!"));
  let vix = $derived(symbolOf("VIX"));
  let stripVenue = $derived<VenueState | null>(
    [dealer?.venue_state, macro?.venue_state, vix?.venue_state]
      .map((state) => servedClosure(state))
      .find((state): state is VenueState => state !== null) ?? null,
  );

  /**
   * The change is derived from the LAST TWO BARS WE ACTUALLY HAVE, never from a session-open price
   * we do not receive. `/api/es/candles` serves bars and nothing else — there is no official prior
   * settle in the payload — so this is labelled "vs prev bar" rather than presented as a day change.
   * Calling a bar-over-bar delta "+0.30%" without saying which baseline it uses is the kind of quiet
   * wrongness that survives because it looks like every other ticker.
   */
  function deltaOf(v: { close: number; prevClose: number | null } | null) {
    if (!v || v.prevClose === null || v.prevClose === 0) return null;
    const abs = v.close - v.prevClose;
    // THREE cases, not two. `abs >= 0` renders a ▲ on a dead-flat tape, which asserts an upward
    // move that did not happen — and it is exactly what a closed market produces, so it would have
    // been on screen every night. Unchanged gets its own neutral glyph and its own (absent) colour.
    return { abs, pct: (abs / v.prevClose) * 100, dir: abs > 0 ? "up" : abs < 0 ? "dn" : "flat" };
  }
  /**
   * The change is measured from WHATEVER NUMBER IS ON SCREEN against the previous bar's close, so
   * the delta and the price can never describe two different things. `vs prev bar` stays literally
   * true in both branches — the baseline is the previous BAR either way; only the near side moves
   * from the bar's close to the live tick.
   */
  let esDelta = $derived(
    headline.px !== null && esLast
      ? deltaOf({ close: headline.px, prevClose: esLast.prevClose })
      : null,
  );
  let vixDelta = $derived(deltaOf(vixLast));

  /* --- REGIME -------------------------------------------------------------------------------
   * §13's headline read: is the dealer book DAMPENING moves or AMPLIFYING them, and how far is the
   * boundary. Everything below comes from `dealer.ts::buildBook` — the SAME module the rail uses,
   * unchanged and un-forked, so the two surfaces are structurally incapable of disagreeing about
   * the regime or the flip. That is why that module was written framework-free.
   */
  let book = $derived(dealer ? buildBook(dealer.strikes, dealer.spot) : null);
  /** The per-instrument verdict, same shape and same rules as the rail and the tide read. */
  let dealerReport = $derived(dealer?.series?.[0] ?? null);
  let stripStale = $derived(
    !stripVenue && [es, vix, dealerReport]
      .some((report) => report?.session_verdict === "STALE"),
  );
  let stripFreshnessTitle = $derived(
    stripStale ? "market strip source stale" : "market strip live",
  );

  /**
   * ⚠ THE 22-POINT TRAP, AND IT IS WHY THIS TERM CARRIES A ROOT LABEL.
   *
   * The flip is a strike on the SPX CASH chain, and the dealer route's spot is SPX cash (the Worker
   * validates the spot payload's ticker against root SPX). The biggest number in this very strip is
   * **ES1!**, the FUTURE. Measured at one instant, 2026-08-07 23:37Z:
   *
   *     ES1! 7777.25 · SPX cash 7755.00 · BASIS 22.25 pts
   *     flip 7790  →  +35.0 from SPX spot  ·  +12.8 from ES
   *
   * A reader who subtracts this distance from the ES price 400px to the left is wrong by 22 points
   * — and wrong in the direction that makes a near boundary look far. "+35" reads as room; "+12.8"
   * reads as now. Two four-digit index points, same typeface, same strip, no way to tell.
   *
   * The merge gate already requires a visible root label wherever SPX/SPXW data renders. Attaching
   * that MANDATORY label to the DISTANCE ITSELF discharges the gate and kills the basis error with
   * the same mark. The absolute strike goes in the title — at a glance you want how far, and the
   * rail already lists GAMMA FLIP with its price in full.
   */
  /**
   * ⚠ THREE OUTCOMES, NOT TWO — CORRECTED 2026-08-08 after `codex review` (P1) found the first
   * version collapsing two DIFFERENT CLAIMS into one null:
   *
   *     book.flip === null   ⇒ "the cumulative curve never changes sign"  — a CLAIM ABOUT THE MARKET
   *     dealer.spot == null  ⇒ "we could not measure spot"                — a CLAIM ABOUT OUR FEED
   *
   * Both produced `null`, and the template rendered `no flip in chain` for both. **So a spot-
   * extraction failure over a chain that genuinely crosses asserted that it never crossed** —
   * a fabricated market fact manufactured out of a missing measurement, which is the exact class
   * `dealer.ts::gammaFlip` refuses in its own header. The payload carries `spot_absent_reason`
   * precisely so a consumer never has to invent this, and the first version did not read it.
   */
  type FlipState =
    | { kind: "flip"; strike: number; delta: number }
    | { kind: "no-flip" }
    | { kind: "no-spot"; reason: string | null };
  let flipState = $derived<FlipState | null>(
    !book || !dealer
      ? null
      : dealer.spot == null
        ? { kind: "no-spot", reason: dealer.spot_absent_reason ?? null }
        : book.flip === null
          ? { kind: "no-flip" }
          : { kind: "flip", strike: book.flip, delta: book.flip - dealer.spot },
  );

  /**
   * The root is read from the PAYLOAD, never hardcoded — SPX-017's ratified semantics ("pooling is
   * DERIVED from data, never hardcoded, and `root_verified` must reach the surface") and D-3's
   * self-retiring pattern. The server currently says "SPX+SPXW pooled-by-vendor"; the identity
   * tokens go on screen and the qualifier goes in the title, because a strip slot cannot carry a
   * 25-character label. If the feed ever becomes root-distinct this renders "SPX" on its own and
   * the pooled affordance disappears by itself — nobody has to remember to delete it.
   */
  let rootLabel = $derived(dealer ? dealer.root.split(/\s+/)[0] : null);

  /**
   * NEUT IS A REAL BRANCH, NOT A ROUNDING CASE. `buildBook` returns FLAT when the net is negligible
   * against the book's own size, and a FLAT book must NOT be coloured as positive — that is the
   * `▲ 0.00` defect this strip already fixed and pinned: **zero may not claim a direction.**
   */
  let regimeWord = $derived(
    !book ? null : book.regime === "POS_GAMMA" ? "POS" : book.regime === "NEG_GAMMA" ? "NEG" : "NEUT",
  );

  /**
   * ⚠ THE SERVER'S `age_s` IS A SNAPSHOT AT SERVE TIME, NOT A LIVE CLOCK — so rendering it raw
   * understates the age by however long ago we fetched, sawtoothing back down on every poll. At a
   * 180s interval that is up to 180s of understatement against a 390s budget.
   *
   * Adding our own elapsed time is NOT a fabrication and the distinction matters: this chip reports
   * POLL age, which is explicitly *a claim about our process* (D-4 — the GEX ladder carries no
   * vendor timestamp, ever). Elapsed-since-our-own-fetch is measured on our own clock about our own
   * process, so the corrected number is the honest one and the raw number is the optimistic one.
   * `nowMs` already ticks once a second for the session clock, so this costs nothing.
   */
  let dealerAgeS = $derived(
    dealer === null || dealerFetchedMs === null
      ? null
      : dealer.age_s + Math.max(0, (nowMs - dealerFetchedMs) / 1000),
  );
  /**
   * The compatibility case `codex review` caught (P2): `series` is OPTIONAL because a rolled-back
   * Worker does not send it. Without this the chip rendered a regime and a poll age and NO verdict
   * at all — so an over-budget book would have looked healthy, which is a law-1 failure (a degraded
   * mode with no visible state). It is deliberately NOT amber: with no server verdict we cannot
   * tell CLOSED from STALE, and asserting a fault we cannot substantiate is the same lie as
   * asserting health. Dashed + the numbers, exactly as an unrecognised verdict is treated.
   */
  /**
   * ⚠ THIS IS A CLAIM ABOUT **OUR** PROCESS AND IT BELONGS ON THE POLL-AGE CHIP, NOT THE VERDICT.
   * Corrected 2026-08-08 (`codex review` P1). The verdict is a SNAPSHOT taken at serve time; the
   * corrected age keeps advancing between polls, so a verdict that was quiet-and-within-budget when
   * served can sit beside a poll age that is now OVER budget — and the first version showed the
   * frozen quiet verdict with no indication of that.
   *
   * The fix deliberately marks the POLL-AGE chip and NOT the verdict chip. Those are two different
   * parties' claims and the whole panel is built on not merging them: the server said what it said,
   * and separately OUR copy of it has aged past the budget. Marking the verdict would be this
   * client computing a competing verdict, which R-3 put server-side on purpose.
   * NOT amber either — over-budget-because-we-have-not-repolled is not a feed fault.
   */
  let overBudget = $derived(
    dealer !== null && dealerAgeS !== null && dealer.budget_s > 0 && dealerAgeS > dealer.budget_s,
  );
  /**
   * ⚠ WHETHER A BUDGET MISS IS **LOUD** IS DECIDED BY THE VERDICT — and getting this wrong in either
   * direction is a defect this repo has already paid for, once each way.
   *
   * `codex review` (P1) was right that my first version broke **law 1: "a budget miss renders as a
   * LOUD chip."** I had reasoned my way to a quiet dashed marker from the false-amber lesson — but
   * that lesson is about SESSION STATE (a shut market is not a fault), not about budget misses, and
   * I applied it one category too wide.
   *
   * The naive fix is equally wrong: on a CLOSED market the ladder's age grows without bound (744s
   * against a 390s budget right now), so unconditional loudness is amber all weekend — the exact
   * defect that kept the tide panel unmounted for a day.
   *
   * ⇒ **THE SETTLED SYNTHESIS IS R-3's: THE SERVER VERDICT DECIDES.** Over budget on a QUIET verdict
   * is the market being shut and stays quiet-but-STATED. Over budget on anything else — LIVE,
   * unrecognised, or null — is a real freshness miss and is LOUD. The number is rendered either way,
   * because law 10 says render what the row proves; only the TONE is conditional.
   *
   * ⚠⚠ AND THE PREDICATE IS **NOT** `verdictTone(...) !== "quiet"` — I WROTE THAT FIRST AND THE
   * AUDIT CAUGHT IT ON ITS FIRST RUN. `QUIET_VERDICTS` contains **LIVE**, so that test excluded the
   * one case that must be loud: an OPEN market whose data is past budget.
   *
   * **This is the exact defect I published as a caveat on `TidePanel:376` and warned BUILDER-l about
   * — forty minutes before writing it into my own new code.** The allowlist answers *"should this be
   * loud?"*; it cannot answer *"does the market excuse this?"* Two predicates over one set, and only
   * one of them cares that LIVE is a member. **A set named for one question does not answer another.**
   *
   * What excuses a budget miss is the market NOT PRODUCING — not the feed being healthy.
   */
  const MARKET_NOT_PRODUCING: readonly string[] = ["CLOSED", "SESSION_END_PUBLISH_LAG"];
  let overBudgetLoud = $derived(
    overBudget && !MARKET_NOT_PRODUCING.includes(dealerReport?.session_verdict ?? ""),
  );

  /**
   * ⚠ THE LOUD BUDGET MISS IS A DEGRADED MODE AND LAW 1 ASKS FOR **BOTH** HALVES — a visible state
   * **AND A LOG LINE.** It had only the first. Found by `codex review --commit 4a28191` (P2), re-run
   * on the COMMITTED artifact by the next seat rather than inherited as green: `loadDealer()` logs a
   * null verdict, a null spot and a fetch error, but a response that arrives SUCCESSFULLY and is
   * already past budget — or that crosses the budget between polls — entered the loud state in
   * silence. **The visible half was proven by 2320 assertions; the logged half did not exist.**
   *
   * ⚠ AND IT LIVES HERE, NOT IN `loadDealer()`, BECAUSE THE CROSSING IS NOT A FETCH EVENT. The
   * budget can be crossed by the 1s clock with no poll in between — that is the whole reason
   * `dealerAgeS` corrects for elapsed time — so a log inside the fetch would miss the common case.
   *
   * ⚠⚠ TRANSITION-LOGGED, NEVER LEVEL-LOGGED. `overBudgetLoud` is recomputed every tick; logging the
   * LEVEL emits ~3,600 lines an hour and law 1's log line becomes the noise that buries the next
   * one. `loudLogged` is a PLAIN `let` on purpose — in runes mode it is not reactive, so this effect
   * cannot depend on its own write and cannot loop.
   *
   * THE QUIET SIDE IS DELIBERATELY NOT LOGGED, and that is the same ruling as the tone: over budget
   * on a shut market is the market, not a degraded mode. Logging it would put a nightly ERROR in the
   * diagnostics for a healthy closed feed — amber-inflation one layer down, in the log instead of
   * on the screen.
   */
  let loudLogged = false;
  $effect(() => {
    if (overBudgetLoud === loudLogged) return;
    loudLogged = overBudgetLoud;
    const verdict = dealerReport?.session_verdict ?? "none served";
    if (overBudgetLoud) {
      console.error(
        `[REGIME] DEGRADED: dealer book is OVER BUDGET — age ${Math.round(dealerAgeS ?? 0)}s ` +
          `> budget ${dealer?.budget_s ?? "?"}s, and verdict=${verdict} does not excuse it ` +
          `(a shut market outruns its budget by design; this one is not shut).`,
      );
    } else {
      console.warn(
        `[REGIME] RECOVERED: dealer book is back inside budget or the market stopped producing — ` +
          `age ${Math.round(dealerAgeS ?? 0)}s / budget ${dealer?.budget_s ?? "?"}s, verdict=${verdict}.`,
      );
    }
  });

  /** NY wall clock + the ES session phase. `session.ts` is ES-ONLY and this IS ES — inside contract. */
  let clock = $derived(
    new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/New_York",
    }).format(nowMs),
  );
  let phase = $derived(sessionAt(nowMs));

  async function load() {
    const [healthResult, esResult, vixResult] = await Promise.allSettled([fetchHealth(), fetchCandles("ES1!", 2), fetchCandles("VIX", 2)]);
    nowMs = Date.now();
    const pick = (bars: { t: number; c: number }[]) =>
      bars.length ? { close: bars[bars.length - 1].c, prevClose: bars.length > 1 ? bars[bars.length - 2].c : null } : null;

    if (healthResult.status === "fulfilled") {
      health = healthResult.value.symbols;
      const servedAt = Date.parse(healthResult.value.served_at);
      healthClockMs = Number.isFinite(servedAt) ? servedAt : nowMs;
      healthFault = null;
    } else {
      healthFault = reportDoorFailure("health", healthResult.reason, healthClockMs, nowMs);
    }
    if (esResult.status === "fulfilled") {
      esLast = pick(esResult.value.bars);
      esClockMs = esResult.value.bars.at(-1)?.t ?? nowMs;
      esFault = null;
    } else {
      esFault = reportDoorFailure("ES1!", esResult.reason, esClockMs, nowMs);
    }
    if (vixResult.status === "fulfilled") {
      vixLast = pick(vixResult.value.bars);
      vixClockMs = vixResult.value.bars.at(-1)?.t ?? nowMs;
      vixFault = null;
    } else {
      vixFault = reportDoorFailure("VIX", vixResult.reason, vixClockMs, nowMs);
    }
  }

  /**
   * ⚠ SEPARATE FROM `load()` AND WITH ITS OWN ERROR SLOT, DELIBERATELY. A dealer failure must not
   * blank the price chips, and a price failure must not blank REGIME — one shared `error` would let
   * either feed's outage misreport the other as broken.
   */
  /**
   * ⚠ THE LOG LINES ARE NOT OPTIONAL AND I SHIPPED WITHOUT THEM. Law 1's full sentence is *"no
   * degraded modes without a visible STALE/ERROR state **AND A LOG LINE**"* — I built every visible
   * state and none of the log lines, so an outage left no trace in operational diagnostics.
   * `codex review` flagged it as a P2 on the first pass and I did not action it; it came back
   * attached to two P1s. **A finding you decline to take does not stop being true.**
   * Non-secret-bearing by construction: statuses, ages and the server's own reason strings only.
   */
  async function loadDealer() {
    try {
      dealer = await fetchDealer("SPX");
      dealerFetchedMs = Date.now();
      dealerError = null;
      const rep = dealer.series?.[0];
      if (rep && rep.session_verdict == null) {
        console.error(
          `[REGIME] DEGRADED: /api/dealer/gex served ${rep.symbol} with NO session_verdict — ` +
            `CLOSED cannot be told from STALE. reason=${rep.session_reason ?? "none served"}`,
        );
      }
      if (dealer.venue_state?.verdict !== "CLOSED" && dealer.spot == null) {
        console.error(
          `[REGIME] DEGRADED: spot unavailable, flip distance not computable ` +
            `(the ladder is intact — this is not a claim about the chain). ` +
            `reason=${dealer.spot_absent_reason ?? "none served"}`,
        );
      }
    } catch (err) {
      const fe = err as FeedError;
      dealerError = fe?.message ?? String(err);
      console.error(
        `[REGIME] FEED ERROR: /api/dealer/gex — ${dealerError}` +
          (fe?.status ? ` (HTTP ${fe.status})` : "") + (fe?.detail ? ` — ${fe.detail}` : ""),
      );
    }
  }

  /**
   * MACRO's loader. The log lines are here from the first commit rather than added after a review
   * found them missing — law 1 is *"no degraded modes without a visible STALE/ERROR state AND A
   * LOG LINE"*, and this file's own header records shipping every visible state and none of the
   * log lines, then taking that back as two P1s.
   *
   * Non-secret-bearing by construction: statuses, dates and the server's own reason strings only.
   *
   * ⚠ POLLED ON THE DEALER CADENCE, NOT THE PRICE ONE. These walls change ONCE A DAY — the route
   * serves `data_date`, and the mirror behind it is a nightly job. Polling a daily figure every
   * 15s would spend request budget to re-fetch a number that cannot have moved.
   */
  async function loadMacro() {
    try {
      macro = await fetchMacroWalls("ES");
      macroError = null;
      const row = macro.products?.[0];
      if (macro.venue_state?.verdict !== "CLOSED" && row && row.age_trusted === false) {
        console.error(
          `[MACRO] DEGRADED: /api/macro/walls served ES with age_trusted=false — ` +
            `the age is WITHHELD on the chip rather than shown as 0. data_date=${row.data_date ?? "none"} ` +
            `on_fallback=${String(row.on_fallback)} sources=${row.sources?.join(",") ?? "unserved"}`,
        );
      }
      if (macro.ok !== false && (macro.products?.length ?? 0) === 0) {
        console.error(
          `[MACRO] DEGRADED: /api/macro/walls answered ok with ZERO products — ` +
            `the mirror served nothing for ES. This is a true answer, not an outage.`,
        );
      }
    } catch (err) {
      const fe = err as FeedError;
      macroError = fe?.message ?? String(err);
      console.error(
        `[MACRO] FEED ERROR: /api/macro/walls — ${macroError}` +
          (fe?.status ? ` (HTTP ${fe.status})` : "") + (fe?.detail ? ` — ${fe.detail}` : ""),
      );
    }
  }

  onMount(() => {
    load();
    loadDealer();
    loadMacro();
    const t = setInterval(load, pollMs);
    const d = setInterval(loadDealer, dealerPollMs);
    const m = setInterval(loadMacro, dealerPollMs);
    const tick = setInterval(() => (nowMs = Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(d); clearInterval(m); clearInterval(tick); };
  });

  /**
   * The absent elements, as DATA rather than markup — so the route each one waits on is stated once,
   * and a slot disappears by deleting its entry rather than by hunting through the template.
   *
   * ⚠ REGIME'S ENTRY IS GONE BECAUSE ITS ROUTE LANDED — that deletion IS the retirement mechanism
   * working as designed, not a special case.
   *
   * ⚠ AND CUSHION'S COPY CHANGED WITH IT. It used to name `/api/dealer/gex` as one of TWO missing
   * inputs. That route now exists, so the old text would have become FALSE ON SCREEN the moment
   * REGIME went live — a comment that lies, in the place a user actually reads. CUSHION still
   * renders NO FEED and that is CORRECT rather than a shortfall: half an input is not an input, and
   * inventing the other half is exactly the lying-value case the row forbids in writing.
   */
  /**
   * ⚠ `needs` IS THE ON-SCREEN NAME AND IT IS NOT OPTIONAL. The row's own words: *"each chip must
   * NAME ITS MISSING ROUTE on screen"*. Until today REGIME was the only slot that did — the other
   * two carried theirs in `title` alone — so retiring REGIME would have quietly left the strip with
   * ZERO slots meeting that requirement. Filling one absence must not disarm the other two.
   * `needs` is the short label that renders; `route` is the full one, in the title.
   */
  /**
   * ⚠ MACRO'S ENTRY IS GONE BECAUSE ITS ROUTE LANDED — the same retirement REGIME went through,
   * and the deletion IS the mechanism (see the note above). It is not a special case.
   *
   * ITS NOTE HAD GONE FALSE IN TWO DIRECTIONS, which is why leaving it would have been worse than
   * an empty slot:
   *   1. "only the route is missing" — `/api/macro/walls` serves ok:true with real ES walls, and
   *      CC-nn measured `macro/walls` appearing ZERO times in the served bundle: the absence was
   *      unconditional in the shipped code, not a fetch that was failing.
   *   2. "NQ/RTY/CL/GC/SI/6E/6A/6J/BTC walls" — superseded. The route's own `coverage_basis` says
   *      ES only BY RULING (R81, re-issued R462(5): SPX-ONLY) and names the rest OFF-PLAN, "not
   *      missing". A chip promising them would re-advertise off-plan work on the owner's surface.
   * ⇒ A note that lies is worse than no note, in the place a user actually reads.
   */
  const ABSENT = [
    { id: "cushion", label: "CUSHION", needs: "an implied-move source", route: "an implied-move source (UW expected move / straddle mid) — no owner", note: "distance-to-flip ÷ expected move — the flip term ARRIVED with /api/dealer/gex; the expected-move source still has no owner, so the chip stays absent rather than shipping half a ratio" },
  ] as const;
</script>

<div class="strip" role="group" aria-label="Market status strip">
  <span class="strip-title">MARKET STATUS</span>
  <span class="pill">ES</span>
  <span class="pill">SPX</span>
  <span class="hypo">HYP</span>
  <PanelFreshness label="market status" venueState={stripVenue} stale={stripStale} ageTitle={stripFreshnessTitle} />
  <AgePill payload={dealer} fetchedAtMs={dealerFetchedMs} door="/api/dealer/gex?root=SPX" />
  {#each stripFaults as fault (fault.door)}
    <span class="err" role="alert"><b>DOOR FAILURE</b> · {fault.label}{#if fault.retained}<small> · {fault.retained}</small>{/if}</span>
  {/each}
  {#if dealerError}
    <span class="regime err" role="alert" title="/api/dealer/gex failed — REGIME is not showing live data">
      <b>REGIME</b><small class="state">FEED ERROR</small><small class="why">{dealerError}</small>
    </span>
  {:else if !dealer}
    <span class="loading-dot" aria-label="market status loading">●</span>
  {/if}
  <PanelInfo id="market-status-info" label="Market status information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
  <!--
    REGIME holds the leftmost, most prominent slot — §13's layout — even while absent. A cockpit that
    quietly hides the unavailability of its headline regime read is worse than one that admits it.
  -->
  {#if dealer}
    <!--
      REGIME holds the leftmost, most prominent slot — §13's layout.

      THE CONTAINER STAYS NEUTRAL AND ONLY THE VALUE CARRIES COLOUR. A chip that tints its whole
      body green or red every session would put a large standing colour field in a strip whose one
      reserved alarm colour is amber; colour belongs on the two characters that mean something.

      LOUD KEYS ON THE SERVER VERDICT, NEVER ON THE `stale` FLAG. This payload is `CLOSED` AND
      `stale: true` at the same instant and BOTH ARE CORRECT — reddening on the flag puts REGIME
      amber every night on a healthy shut market. That exact defect shipped LIVE on the dealer rail
      on 08-07 and was fixed hours later; it is not being reintroduced one slot over.
    -->
    <span class="regime lead">
      <b>REGIME</b>
      <span class="val" class:pos={regimeWord === "POS"} class:neg={regimeWord === "NEG"}
            title={regimeWord === "POS"
              ? "Net dealer gamma is POSITIVE — dealer hedging DAMPENS moves"
              : regimeWord === "NEG"
                ? "Net dealer gamma is NEGATIVE — dealer hedging AMPLIFIES moves"
                : "Net dealer gamma is negligible against the book's own size — no direction is claimed"}>
        {regimeWord} γ
      </span>

      <span class="flip">
        {#if flipState?.kind === "flip"}
          <!-- Distance. The strike itself is detail and lives in the title — RESTORED after the
               refactor dropped it (`codex review` P2); the design board §73-75 specifies it. -->
          <span title="Gamma flip at strike {flipState.strike} · spot {dealer.spot} · distance measured in SPX index points, NOT ES">flip
            <span class="num">{flipState.delta > 0 ? "+" : "−"}{Math.abs(flipState.delta).toFixed(0)}</span></span>
        {:else if flipState?.kind === "no-spot"}
          <!--
            ⚠ A CLAIM ABOUT OUR FEED, NOT ABOUT THE MARKET. The ladder arrived; spot did not, so the
            DISTANCE cannot be computed — but the chain may well cross. Saying "no flip" here would
            assert a market fact out of a missing measurement. The server's own reason is rendered
            verbatim in the title; this panel does not compose one.
          -->
          <!--
            ⚠ LOUD, not muted — `codex review` P1, and the desk already ruled this shape for the
            tide (cb5c649f): *"a LOUD degrade when spot is absent — never draw one series silently."*
            My first version rendered it in `.muted-note`, the treatment reserved for NO-FLIP, which
            is a real MARKET STATE. **Spot extraction failing is OUR FEED failing**, and dressing a
            feed failure in the benign-market-state style is the original defect surviving into the
            styling after being fixed in the words.
          -->
          <span class="loudnote" title="SPOT IS UNAVAILABLE — the distance to the flip cannot be computed. This says NOTHING about whether the chain crosses. Server reason: {flipState.reason ?? 'none served'}">flip — NO SPOT</span>
        {:else if flipState?.kind === "no-flip"}
          <!-- A whole chain of one sign is a REAL market state, not a missing value (dealer.ts). -->
          <span class="muted-note" title="Cumulative net gamma never changes sign across the chain — there is no flip to show, and inventing one would be a fabricated level">no flip in chain</span>
        {/if}
        <!--
          ⚠ THE ROOT LABEL IS HOISTED OUT OF THE FLIP BRANCH — `codex review` P1, 2026-08-08.
          It used to live INSIDE the flip-present case, so the no-flip state rendered SPX/SPXW-derived
          data with NO ROOT AT ALL — breaking the merge gate this seat quoted in its own design board
          ("root labels visible wherever SPX/SPXW data renders"), in the build whose headline finding
          was that the root label is load-bearing. **REGIME is root-labelled in EVERY state that
          renders dealer data, not only the one where a distance happens to exist.**
        -->
        <span class="root" class:pooled={dealer.root_verified === false}
              title={dealer.root_verified
                ? `root ${dealer.root}`
                : `root ${dealer.root} — this feed is NOT root-separated, so SPX and SPXW return identical payloads upstream (D-3)`}>{rootLabel}</span>
      </span>

      <!-- Law 6. Nothing here is CALIBRATED; same marker the dealer rail's header carries. -->
      <span class="hypo" title="HYPOTHESIS — E1/E2/E4 pre-registered studies pending. Not calibrated.">HYP</span>

      {#if dealerReport && dealerReport.session_verdict != null}
        <span class="verdict" class:loud={verdictTone(dealerReport.session_verdict) === "loud"}
              class:quiet={verdictTone(dealerReport.session_verdict) === "quiet"}
              class:unknown={verdictTone(dealerReport.session_verdict) === "unknown"}
              title="{dealerReport.session_reason ?? 'no reason served'} · {dealerReport.symbol} · budget {dealerReport.budget_s}s {dealerReport.budget_basis}">
          {dealerReport.session_verdict}
        </span>
      {:else if dealerReport}
        <!--
          ⚠ `series[0]` PRESENT WITH `session_verdict: null` — a REAL case, not a gap in the union
          (`api.ts::SessionVerdict` says so), and it happens exactly when the spot source this
          verdict is derived from is unavailable. `codex review` P1, 2026-08-08: the first version
          fell into the branch above, where `verdictTone(null)` returns `"none"`, every state class
          evaluated false, and the chip rendered as a SUBDUED BASE PILL with the reason only in a
          hover title. **A degraded mode with no visible state is law 1's exact prohibition.**
          Rendered DASHED and named, never amber — no verdict means we cannot tell CLOSED from
          STALE, and asserting a fault we cannot substantiate is the same lie as asserting health.
        -->
        <span class="verdict unknown"
              title="The server served this instrument's report with NO session verdict — typically because the spot source it derives from is unavailable. CLOSED cannot be told from STALE, so nothing is asserted in either direction. Reason: {dealerReport.session_reason ?? 'none served'}">
          {dealerReport.symbol} NO VERDICT
        </span>
      {:else}
        <!--
          ⚠ THE ROLLED-BACK-WORKER PATH, and it must never render as health. `series` is optional in
          the contract, so a Worker older than the 23:1xZ deploy sends none. Without this branch the
          chip showed a regime and an age and NOTHING about the verdict — an over-budget book would
          have read as fine, which is a degraded mode with no visible state (law 1). Found by
          `codex review`, not by me. DASHED, NOT AMBER: with no verdict we cannot tell CLOSED from
          STALE, and asserting a fault we cannot substantiate is the same lie as asserting health.
        -->
        <span class="verdict unknown" class:over={overBudget}
              title="This Worker served no per-instrument verdict for the dealer book (a rolled-back read-API does not send `series`). Without one, CLOSED cannot be told from STALE — so no verdict is asserted here in either direction.">
          NO VERDICT{overBudget ? ` · OVER BUDGET ${formatAge(dealer.budget_s)}` : ""}
        </span>
      {/if}
      <!--
        POLL AGE, LABELLED — and it is not a choice. D-4: `/greek-exposure/strike` carries NO time
        field at all (only a calendar `date`), so `vendor_ts` is null ALWAYS and FORWARD by design.
        A vendor-time chip here would be fabricated provenance — the precise thing the retired
        interim writer did when it stamped a timestamp derived from that `date`.
      -->
      <!--
        ⚠ THE OVER-BUDGET MARKER LIVES HERE, ON OUR OWN CLAIM — not on the verdict chip.
        `codex review` P1: the verdict is a snapshot taken at serve time while this age keeps
        advancing, so a quiet verdict could sit beside a silently over-budget age. Marking the
        VERDICT would make this client compute a competing one, which R-3 put server-side on
        purpose; marking the POLL AGE says the true thing — the server said what it said, and
        separately OUR copy has aged past the budget it publishes. Not amber: needing a re-poll
        is not a feed fault.
      -->
      <!--
        ⛔ THE CHIP NAMES ITS RAIL, AND THAT WORD IS THE WHOLE FIX (DESK-n21 R129).
        2026-08-18 ~22:42Z the owner read "poll 9m57s · OVER BUDGET 6m30s" here and took it for
        the PAGE's heartbeat — then reported the chart as glitchy. Measured after: the numbers are
        the DEALER book's own `age_s`/`budget_s` and reproduce his screen to the second (budget_s
        390 = 6m30s, exact). ⇒ ***THE CHIP WAS HONEST, CORRECT, AND FIRING ABOUT THE ONE RAIL HE
        WAS NOT LOOKING AT.*** R127 first ruled it "a global heartbeat"; that premise was struck on
        this measurement — nothing about WHAT it reads was ever wrong.
        ⇒ ***A TRUE STATEMENT IN A GLOBAL POSITION IS READ AS A GLOBAL STATEMENT.*** The defect was
        never the number, so the cure does not touch the number: it names the subject.
        ⚠ The sibling chips in TidePanel/DealerRailPanel stay BARE on purpose — a chip inside its
        own panel inherits that panel's identity. This one has no such parent. Naming all three
        would add noise where there is no ambiguity, which is how a real fix becomes a habit.
        ⚠ NO UNIT ARM IS POSSIBLE: this is a literal in a .svelte template and no test can see it
        (aa's own M10 bound). Its witness is the SERVED DOM — boot the deployed page and read the
        chip face. Stated so nobody records this as covered when it is only verified.
      -->
      <span class="pollage" class:over={overBudget} class:loud={overBudgetLoud}
            title="DEALER BOOK poll age. How long since the read-API received this book, corrected for how long ago WE fetched it. A claim about our process and about THIS RAIL ONLY — never about the market, and never about the other rails on this page; this source carries no vendor timestamp.{overBudget ? ` · OVER the ${dealer.budget_s}s budget this response publishes${overBudgetLoud ? '' : ' — quiet because the server verdict is quiet (a shut market outruns its budget by design)'}` : ''}">dealer poll {formatAge(dealerAgeS ?? dealer.age_s)}{overBudget ? ` · OVER BUDGET ${formatAge(dealer.budget_s)}` : ""}</span>
    </span>
  {/if}

  <!-- ES1!: the one number read at a glance. Largest thing in the strip, tabular so it cannot jitter. -->
  <span class="bigpx">
    <span class="sym">ES1!</span>
    <span
      bind:this={stripPrice}
      class="px num"
      data-strip-price={headline.px ?? undefined}
      data-strip-tick-ms={tickMs ?? undefined}>{headline.px !== null ? headline.px.toLocaleString(undefined, { minimumFractionDigits: 2 }) : "—"}</span>
    <!--
      WHICH SOURCE THIS NUMBER CAME FROM, AND HOW OLD IT IS — adjacent to the figure, never only in
      a chip at the edge of the panel. A reader who screenshots or quotes the price takes this with
      it; a region-level staleness chip stays behind.
    -->
    {#if headlineMark}
      <span class="basis"
            title={headline.source === "tick"
              ? "The last TRADE on the tick tape, with the age of that trade's own row. Not a 1m bar close."
              : "The close of the last 1m bar the spine published. It changes once per minute by construction; no tick was current enough to offer."}>{headlineMark}</span>
    {/if}
    {#if esDelta}
      <!-- Sign is carried by a GLYPH as well as colour: colour alone vanishes for a CVD reader. -->
      <span class="chg num" class:up={esDelta.dir === "up"} class:dn={esDelta.dir === "dn"}>
        {esDelta.dir === "up" ? "▲" : esDelta.dir === "dn" ? "▼" : "="} {Math.abs(esDelta.abs).toFixed(2)} ({esDelta.pct.toFixed(2)}%)
        <span class="basis">vs prev bar</span>
      </span>
    {/if}
    {#if es}
      <span class="verdict" class:loud={verdictTone(es.session_verdict) === "loud"}
            class:quiet={verdictTone(es.session_verdict) === "quiet"}
            class:unknown={verdictTone(es.session_verdict) === "unknown"}
            title={es.session_reason ?? "no reason served"}>
        {es.session_verdict ?? "NO VERDICT"}
      </span>
    {/if}
  </span>

  <!--
    VIX. Its verdict and budget come from the SERVER, per instrument — never from `session.ts`, which
    is ES-ONLY. VIX settles 20:15Z while ES trades to 23:59Z, so running the CME calendar against VIX
    reports "session open" with total confidence; a BUILDER seat filed VIX's normal close as a fault
    twice before anyone noticed the calendar was the bug. Its 450s budget is `derived-from-band`
    (SPX-012) and is the server's number, not one this component picks.
  -->
  <span class="kchip">
    <span class="lbl">VIX</span>
    <span class="num">{vixLast ? vixLast.close.toFixed(2) : "—"}</span>
    {#if vixDelta}
      <span class="num chg" class:up={vixDelta.dir === "up"} class:dn={vixDelta.dir === "dn"}>
        {vixDelta.dir === "up" ? "▲" : vixDelta.dir === "dn" ? "▼" : "="}{Math.abs(vixDelta.abs).toFixed(2)}
      </span>
    {/if}
    {#if vix && vix.venue_state?.verdict !== "CLOSED"}
      <span class="verdict" class:loud={verdictTone(vix.session_verdict) === "loud"}
            class:quiet={verdictTone(vix.session_verdict) === "quiet"}
            class:unknown={verdictTone(vix.session_verdict) === "unknown"}
            title="{vix.session_reason ?? 'no reason served'} · budget {vix.budget_s}s {vix.budget_basis}">
        {vix.session_verdict ?? "NO VERDICT"}{vix.age_s !== null ? ` ${formatAge(vix.age_s)}` : ""}
      </span>
    {/if}
  </span>

  <!-- The clock is not a feed: no staleness chip. A chip here would be chrome posing as instrumentation. -->
  <span class="kchip">
    <span class="lbl">NY</span>
    <span class="num">{clock}</span>
    <span class="ph">{phase.label}</span>
  </span>

  <span class="spacer"></span>

  <!--
    MACRO — the walls the CME book is defending, from `/api/macro/walls`.

    IT SITS BEFORE THE ABSENT SLOTS, NOT AMONG THEM. Its entry left `ABSENT` when its route landed;
    rendering it beside the dashed chips would keep it reading as unfinished to anyone scanning the
    strip's right edge, which is the whole signal that grouping carries.

    THE DATE IS THE SESSION, NOT THE COPY TIME — the one decision `macroWalls.ts` exists to hold.
  -->
  {#if macroState.kind === "error"}
    <!-- Built-and-broken is FEED ERROR, never NO FEED. Same law as REGIME's branch above: the
         unfinished-state is ignorable by design, and hiding a live outage inside it is that lie
         with its sign flipped. -->
    <span class="macro err" role="alert" title={macroWallsTitle(macroState)}>
      <b>MACRO</b>
      <small class="state">FEED ERROR</small>
      <small class="why">{macroState.message}</small>
    </span>
  {:else if macroState.kind === "loading"}
    <span class="macro pending" title={macroWallsTitle(macroState)}>
      <b>MACRO</b><small class="state">loading…</small>
    </span>
  {:else if macroState.kind === "empty"}
    <!-- A served empty is a TRUE answer about the mirror. It does not go silent and it does not
         alarm: ax's Saturday warning is that `ok:false` on a shut market "will look like a bad
         deploy", and a blank slot would look like a regression in the chip that just shipped. -->
    <span class="macro quiet" title={macroWallsTitle(macroState)}>
      <b>MACRO</b><small class="state">NO WALLS</small><small class="why">{macroState.why}</small>
    </span>
  {:else}
    <span class="macro" title={macroWallsTitle(macroState)}>
      <b>MACRO</b>
      <span class="walls">
        <!-- Put first, call second: low→high, the ladder's own order. The letters do the naming so
             no colour has to carry a convention the data does not assert — see the style note. -->
        <span title="put wall — the strike carrying the largest put open interest">
          <span class="wl">P</span>&nbsp;{macroState.putWall ?? "—"}
        </span>
        <span class="sep" aria-hidden="true">·</span>
        <span title="call wall — the strike carrying the largest call open interest">
          <span class="wl">C</span>&nbsp;{macroState.callWall ?? "—"}
        </span>
      </span>
      {#if macroState.source === "FALLBACK"}
        <small class="asof stale">FALLBACK</small>
      {/if}
      <!-- ⚠ THE DATE IS `data_date`, THE SESSION THESE WALLS DESCRIBE. Never `mirrored_at`: the
           mirror re-runs every few minutes over a daily book, so a copy-time here would read
           "seconds old" over day-old walls. Armed as M1. -->
      <!--
        ⚠ THE SEPARATOR IS `&nbsp;·&nbsp;` AND IT TOOK TWO WRONG TRIES TO GET THERE:
          1. `" · {age}"` as a text node → Svelte trimmed the leading space: `2026-08-22· 1d old`.
          2. `.age::before { content: " · " }` → renders correctly BUT pseudo-elements are invisible
             to `innerText`, so the audit read back `2026-08-221d old` — pixels right, READBACK
             fused. This file's own header warns that reading the copy is not verifying it; a
             separator only a human can see fails the instrument in the other direction.
        Entities survive BOTH the compiler's trim and the readback. Caught by reading the RED run's
        rendered text, which is the only reason either version was ever seen.
      -->
      <small class="asof" class:stale={macroState.stale}>
        {macroState.dataDate ?? "date unserved"}{#if macroAgeLabel(macroState)}<span class="age">&nbsp;·&nbsp;{macroAgeLabel(macroState)}</span>{/if}
      </small>
    </span>
  {/if}

  {#each ABSENT as a (a.id)}
    <span class="nofeed" title="{a.note} — waiting on {a.route}">
      <span class="glyph" aria-hidden="true">○</span>
      <b>{a.label}</b>
      <!-- ⚠ ONLY THE ROUTE TAIL DROPS ON MOBILE, NEVER `.state`. The first version of this hid
           `small` wholesale and took the words "NO FEED" with it, so the ABSENCE ITSELF went
           invisible at 390px — 48 audit failures, all mobile. The state is the words. -->
      <small class="state">NO FEED</small><small class="route">· {a.needs}</small>
    </span>
  {/each}
  <p>CUSHION · NO FEED · an implied-move source</p>
  <p>staleness budgets — price 90s · dealer 3m · flow 3m · news 2m · SPX ≠ SPXW: root labeled on every panel · detection ≠ automation — nothing here places an order</p>
  </PanelInfo>
</div>

<style>
  .strip {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 9px 20px;
    border-bottom: 1px solid var(--line);
    background: var(--surface);
    flex-wrap: wrap;
  }
  /* Density is presentation and is stamped on <html> by workspace.ts — never prop-drilled. It changes
     RHYTHM only: both workspaces show the same seven slots, because which elements render is a
     data-visibility decision, not a presentation one. §13's "Scott gets things collapsed" governs
     PANELS; workspace.ts's collapsed list holds panel ids (news, flow), and the strip is not one. */
  :global(html[data-density="comfortable"]) .strip { gap: 20px; padding: 12px 20px; }

  .bigpx { display: flex; align-items: baseline; gap: 8px; }
  .bigpx .sym { font-size: 11px; letter-spacing: 0.12em; color: var(--muted); }
  .bigpx .px { font-size: 22px; font-weight: 700; }
  .chg { font-size: 12px; font-weight: 600; }
  .chg.up { color: var(--up); }
  .chg.dn { color: var(--dn); }
  /* `nowrap` because at 390px this broke as "vs prev / bar" — an orphaned word under a price is
     the kind of sloppiness a reader registers without being able to name. Caught in the shot pass. */
  .basis { color: var(--faint); font-weight: 400; font-size: 10px; letter-spacing: 0.04em; white-space: nowrap; }

  .kchip {
    display: flex;
    align-items: baseline;
    gap: 6px;
    background: var(--chip);
    border: 1px solid var(--line);
    border-radius: var(--radius-chip);
    padding: 4px 10px;
    font-size: 12px;
  }
  .kchip .lbl { color: var(--muted); font-size: 10px; letter-spacing: 0.1em; }
  /* ⚠ --muted, NOT --faint. This span renders INSIDE `.kchip`, whose background is `--chip`,
     where --faint measures 3.93:1 (light) / 4.01:1 (dark) — BELOW AA for 10px text. --muted is
     4.99:1 / 5.01:1 on --chip. Found by GK-i 2026-08-09.
     ⇒ THE REASON MY OWN CENSUS MISSED IT: this rule sets the COLOUR and the BACKGROUND is set one
     level up, on the parent. A contrast pair is established by the MARKUP, not by a single rule —
     so a rule-local search can never find it. See :688's note, whose "the .kchip siblings can
     afford --chip because their content is --ink/--muted" was false about THIS sibling. */
  .ph { color: var(--muted); font-size: 10px; letter-spacing: 0.06em; }

  .verdict {
    font-size: 10px;
    letter-spacing: 0.06em;
    font-weight: 600;
    color: var(--muted);
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid var(--line);
    background: var(--surface-2);
    white-space: nowrap;
  }
  /* Only STALE is loud. CLOSED and the server's named states read as INFORMATION — a market being
     shut is not a fault, and an amber that fires on schedule every night is one people stop reading. */
  .verdict.loud {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border-color: var(--stale);
    font-weight: 700;
  }
  /* A verdict this client has not been taught. Visible and DASHED ("cannot vouch for this"), never
     amber — asserting a fault we cannot substantiate is the same lie as asserting health. */
  .verdict.unknown { color: var(--ink); background: var(--chip); border-style: dashed; }
  /* No verdict AND past budget. Still not amber — see the template note — but it must not sit at
     the same weight as a merely-unrecognised state, because there is now a number saying so. */
  .verdict.unknown.over { border-color: var(--faint); font-weight: 700; }

  /* --- REGIME ------------------------------------------------------------------------------
     The retired slot. It reads as a member of the same chip family as VIX and NY — solid border,
     --chip fill — because it IS one now; the dashed/transparent treatment meant "not a thing yet"
     and that is no longer true. One loud element (the value) over four subordinate ones. */
  .regime {
    display: flex;
    align-items: baseline;
    /* ⚠ `wrap` + `min-width: 0` ARE THE FIX FOR A MEASURED 390px OVERFLOW, not defensive noise.
       REGIME carries five parts (value · flip+root · HYP · verdict · poll age) and a non-wrapping
       flex row's min-width is its CONTENT width — so at 390px the strip pushed the document into
       horizontal scroll. Caught by the audit in the `neg` and comfortable-density cases, which is
       the one place it bites: Scott's workspace widens the gaps and tips it over. §19 makes the
       PWA a first-class surface, so this is a defect there, not a cosmetic detail. */
    flex-wrap: wrap;
    min-width: 0;
    gap: 7px;
    padding: 4px 10px;
    border: 1px solid var(--line);
    border-radius: var(--radius-chip);
    /* ⚠ `--surface`, NOT `--chip`, AND THAT IS A CONTRAST DECISION WITH A MEASUREMENT BEHIND IT.
       `--faint` is documented at 4.66:1 — but that is ON `--surface`. Measured on `--chip` it is
       3.93:1 (light) / 4.01:1 (dark), i.e. BELOW AA for the 10px root label and poll age this chip
       carries. ⛔ ***THE NEXT CLAUSE USED TO READ "the `.kchip` siblings can afford `--chip` because
       their content is `--ink`/`--muted`" AND IT WAS FALSE ABOUT THIS VERY FILE*** — `.ph`, a
       `.kchip` sibling four lines up the markup, was `--faint` on `--chip` at 3.93:1 until GK-i
       measured it 2026-08-09. It is now `--muted`, so the claim is TRUE — but it was an untested
       assertion wearing the authority of the measurement above it.
       ⇒ ⭐ **A COMMENT THAT JUSTIFIES A LOCAL DECISION BY ASSERTING A GLOBAL INVARIANT HAS CREATED
       AN UNTESTED CLAIM.** The measurement was right, the reasoning was right, and the SCOPE
       SENTENCE was wrong. If you widen a justification past the thing you measured, measure the
       wider thing or say you did not. Same class as the palette receipt's own `--stale`-on-`--stale-soft` finding
       (3.14:1, which is why `--stale-ink` exists). Outlined rather than filled also reads correctly
       for the LEAD slot — it is not one of the small key chips. */
    background: var(--surface);
    font-size: 11px;
  }
  .regime b { font-size: 11px; letter-spacing: 0.08em; font-weight: 600; color: var(--muted); }
  .regime.lead b { font-size: 12px; }

  /* THE one element that earns colour in this chip.
   *
   * ⚠ COLOUR IS ON THE FIELD AND THE RULE, NOT ON THE TEXT, AND THAT IS THE WHOLE POINT.
   * `--up`/`--dn` are validated as DATAVIZ MARK colours at ≥3:1 (PALETTE_VALIDATION_2026-08-06),
   * NOT as text colours at 4.5:1 — measured as text they run 3.22–4.31:1 and FAIL AA. The package
   * already solved this shape once: `--stale` stayed the border/rule colour and `--stale-ink` was
   * added for text on it. There is no `--up-ink`, so this chip uses the tokens the way they were
   * validated — soft fill + 1px saturated rule (both non-text, ≥3:1) with `--ink` doing the
   * reading at 9.4–11.6:1. Structurally identical to `.verdict.loud`, which is not a coincidence.
   *
   * Identity is never colour-alone (binding rule 1): the WORD carries it and `γ` names the
   * dimension, so a CVD reader loses nothing if the hue does.
   *
   * NEUT is deliberately UNCOLOURED — spending a series colour to say "no signal" burns the
   * palette's scarcest resource on the absence of information, and §13c is about to add a second
   * level family to this cockpit. */
  .regime .val {
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
    letter-spacing: 0.04em;
    padding: 1px 7px;
    border-radius: var(--radius-pill);
    border: 1px solid var(--line);
    background: var(--chip);
  }
  .regime .val.pos { background: var(--up-soft); border-color: var(--up); }
  .regime .val.neg { background: var(--dn-soft); border-color: var(--dn); }

  .regime .flip { font-size: 10px; color: var(--muted); letter-spacing: 0.04em; white-space: nowrap; }
  .regime .flip .num { font-weight: 600; }
  /* The root label is load-bearing, not decoration: it is the merge gate's requirement AND the
     thing that stops a reader measuring an SPX-cash distance off the ES price in the next slot. */
  .regime .root { color: var(--faint); letter-spacing: 0.06em; }
  /* Dashed underline = "there is more to say here", driven by the payload's own boolean so it
     disappears by itself the day the feed becomes root-distinct. */
  .regime .root.pooled { text-decoration: underline dashed var(--faint); text-underline-offset: 2px; }
  .regime .muted-note { color: var(--faint); font-style: italic; }

  .regime .hypo {
    font-size: 9px;
    letter-spacing: 0.08em;
    border-radius: 4px;
    padding: 1px 5px;
    border: 1px solid var(--faint);
    color: var(--muted);
  }
  /* Poll age recedes and says what it is: a claim about OUR process. Same treatment as the rail. */
  .regime .pollage { font-size: 10px; letter-spacing: 0.06em; color: var(--faint); white-space: nowrap; }
  /* Over OUR budget. Promoted out of --faint so it is legible, DASHED so it reads as a qualification
     rather than an alarm, and deliberately NOT amber — see the template note. */
  .regime .pollage.over {
    color: var(--muted);
    border: 1px dashed var(--line);
    border-radius: 4px;
    padding: 1px 5px;
    font-weight: 600;
  }
  /* LAW 1: a budget miss renders as a LOUD chip. Conditional on the verdict NOT being quiet —
     see the `overBudgetLoud` note; unconditional loudness is amber all weekend. */
  .regime .pollage.over.loud {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border: 1px solid var(--stale);
    font-weight: 700;
  }
  /* A degraded FEED, not a market state. Loud by construction — colour, border AND weight. */
  .regime .loudnote {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border: 1px solid var(--stale);
    border-radius: 4px;
    padding: 1px 6px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  /* Loud, and NOT the NO FEED treatment — see the template note. A built-and-broken route is a
     fault; the dashed state means unfinished and would hide this. */
  .regime.err {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border-color: var(--stale);
  }
  .regime.err b { color: var(--stale-ink); }
  .regime.err .state { font-weight: 700; letter-spacing: 0.06em; }
  .regime.err .why { color: var(--stale-ink); opacity: 0.85; font-size: 10px; }
  /* Pre-first-payload. Quiet: nothing has failed and nothing is missing — we simply have not
     answered yet, and dressing that as either would be a lie in one of two directions. */
  .regime.pending .state { color: var(--faint); font-size: 10px; }

  /* THE SEVENTH STATE. Dashed = not-a-thing-yet, transparent = it does not own the filled look the
     live chips have, --faint = present but subordinate. Absence is visible and legible without
     turning a healthy-but-unfinished cockpit into a wall of alarm. */
  .nofeed {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 4px 10px;
    border: 1px dashed var(--line);
    border-radius: var(--radius-chip);
    background: transparent;
    color: var(--faint);
    font-size: 11px;
  }
  .nofeed b { font-size: 11px; letter-spacing: 0.08em; font-weight: 600; color: var(--muted); }
  .nofeed small { font-size: 10px; letter-spacing: 0.04em; }
  .nofeed .glyph { color: var(--faint); }
  /* `.nofeed.lead` DELETED 2026-08-08: it existed only for REGIME's absent state, and REGIME is no
     longer absent. The lead prominence moved to `.regime.lead` with the live chip. Leaving a rule
     for a state that can no longer occur is how quarantined code rots — §1's own lesson. */

  /* MACRO — a LIVE chip, so it takes the filled look the other live slots have rather than the
     dashed absent one. Law 8: every colour here is a token, no literals. */
  .macro {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 4px 10px;
    border: 1px solid var(--line);
    border-radius: var(--radius-chip);
    font-size: 11px;
  }
  .macro b { font-size: 11px; letter-spacing: 0.08em; font-weight: 600; color: var(--muted); }
  .macro small { font-size: 10px; letter-spacing: 0.04em; }
  .macro .walls { display: inline-flex; align-items: baseline; gap: 5px; font-weight: 600; }
  /**
   * ⚠ THE WALLS ARE DELIBERATELY NOT COLOURED, AND THE FIRST DRAFT OF THIS CHIP GOT IT WRONG.
   * I reached for `--up`/`--dn` — the candle and ±GEX palette — which would have painted the put
   * wall red and the call wall green. THOSE COLOURS CARRY A DIRECTIONAL CLAIM AND THESE NUMBERS
   * DO NOT MAKE ONE: a put wall is a strike with large put OI, not a bearish reading, and the
   * convention is genuinely inverted between tools. Borrowed colour would have asserted a market
   * view the route never served — the lying-value case in a new costume.
   * ⇒ The letters P and C name them, position keeps the ladder's low→high order, and the title
   *   spells both out. Neutral ink, no convention to memorise, no claim made.
   * (This is also why the Law 8 gate is worth having: it failed on `--down` not existing, and the
   *  undefined var would have fallen back SILENTLY to inherited ink — the right pixels for the
   *  wrong reason, hiding the design error underneath a passing eye.)
   */
  .macro .wl { color: var(--faint); font-weight: 600; font-size: 10px; }
  .macro .sep { color: var(--faint); }
  .macro .asof { color: var(--faint); }
  /* Amber ONLY when the server says stale. Keyed on the served verdict, never on our own clock —
     the flag-vs-verdict defect that put REGIME amber every night on a healthy shut market. */
  .macro .asof.stale { color: var(--stale-ink); }
  .macro .age { color: var(--faint); }
  .macro.pending .state { color: var(--faint); font-size: 10px; }
  /* A served-empty is quiet, not alarmed: nothing failed. */
  .macro.quiet { border-style: dashed; color: var(--faint); }
  .macro.quiet .why { color: var(--faint); }
  .macro.err {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border-color: var(--stale);
  }
  .macro.err b { color: var(--stale-ink); }
  .macro.err .state { font-weight: 700; letter-spacing: 0.06em; }
  .macro.err .why { color: var(--stale-ink); opacity: 0.85; font-size: 10px; }

  .err {
    font-size: 11px;
    font-weight: 700;
    color: var(--stale-ink);
    background: var(--stale-soft);
    border: 1px solid var(--stale);
    border-radius: 4px;
    padding: 2px 8px;
  }
  .spacer { flex: 1; }

  /* §19 makes the PWA first-class. Seven slots do not fit one row at 390px, so the wrap is DESIGNED
     rather than emergent: the absent slots drop their explanatory tail (kept in `title`) because the
     label and the dashed border already carry the state — the route name is detail, not signal. */
  @media (max-width: 700px) {
    .strip { gap: 10px; padding: 8px 12px; }
    .bigpx .px { font-size: 18px; }
    /* ⚠ ONLY THE ROUTE DROPS. The first version of this hid `small` wholesale, which took the
       words "NO FEED" with it — at 390px the chips read a bare "○ REGIME" in a dashed box, so the
       ABSENCE ITSELF became invisible on the surface §19 makes first-class. The design board said
       the TAIL may drop because "the label and the dashed border carry the state"; the state is the
       words, and the route is the detail. Caught by the audit, 48 failures, all mobile. */
    .nofeed .route { display: none; }
    /* ⚠ MACRO DROPS ITS EXPLANATORY TAIL AND NOTHING ELSE. The walls and the DATE both stay: the
       date is not decoration here, it is the honesty — a walls figure with its session hidden is
       exactly the fresh-looking stale number this chip was built to refuse. Same reasoning that
       keeps `.state` on the absent chips: the tail is detail, the state is the words. */
    .macro.quiet .why { display: none; }
    .spacer { display: none; }
  }
</style>
